import {Component, OnInit, Optional, ViewChild} from '@angular/core';
import {EtlExportUpdateComponent} from '../etl-export-update.component';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {
    ConfiguratorDatastoreService,
    ConfiguratorEtlService,
    EtlConf,
    EtlExportDescDTO,
    FileProcessingLoadConf,
    SaveQueryExtractConf
} from '@tj/tj-dmp-lib-api';
import {
    SelectAffectedPartnersComponent
} from '../../../../components/select-affected-partners/select-affected-partners.component';
import {AccountService} from '../../../../auth/account.service';
import {isAdminOfTenant} from '../../../../auth/admin-levels';
import {filter, switchMap, take} from 'rxjs/operators';

@Component({
    selector: 'dmp-etl-export-update-description',
    templateUrl: './etl-export-update-description.component.html',
    styleUrls: ['./etl-export-update-description.component.scss'],
    standalone: false
})
export class EtlExportUpdateDescriptionComponent implements OnInit {

  actualPartner: string;
  actualOwner: string;
  tenant: string;
  form: FormGroup;
  isAuthorizedAdmin: boolean;
  currentEtl: EtlConf;
  newEtl: boolean;
  /** Affilié par défaut du tenant sélectionné. */
  defaultPartnerCode: string = null;
  @ViewChild('affectedPartnersSettings') affectedPartnersSettings: SelectAffectedPartnersComponent;
  constructor(@Optional() public parent: EtlExportUpdateComponent,
              private fb: FormBuilder,
              private readonly configuratorEtlService: ConfiguratorEtlService,
              private readonly configuratorDatastoreService: ConfiguratorDatastoreService,
              private readonly accountService: AccountService) {
  }

  ngOnInit(): void {
    if (this.parent.id) {
      // Une seule initialisation : le rechargement de l'ETL déclenché après enregistrement ne
      // doit pas reconstruire le formulaire ni réabonner ses valueChanges.
      this.parent.etl$.pipe(filter(etl => !!etl), take(1)).subscribe(etl => {
        this.currentEtl = etl;
        this.newEtl = false;
        
        this.accountService.fetch().subscribe(account => {
          this.initForm();
          this.initTenant();
          this.isAuthorizedAdmin = isAdminOfTenant(account, this.tenant);
          this.handleTenantChanges();
          this.subscribeToEtlChanges();
        });
      });
    } else {
      this.newEtl = true;
      this.accountService.fetch().subscribe(account => {
        this.initForm();
        this.initTenant();
        this.isAuthorizedAdmin = isAdminOfTenant(account, this.tenant);
        this.handleTenantChanges();
        this.subscribeToEtlChanges();
      });
    }
  }

  private initForm(): void {
    this.form = this.fb.group({
      id: [''],
      name: ['', Validators.required],
      description: [''],
      tenant: ['', Validators.required],
      partner: ['', Validators.required],
      view: ['', Validators.required],
      exportPartners: [[]],
      exportMode: [EtlConf.ExportModeEnum.GROUPED]
    });
    this.form.get('exportPartners').valueChanges.subscribe(() => this.applyExportModeAvailability());
  }

  private initTenant(): void {
    this.tenant = this.parent?.tenant || '';

    if (this.tenant) {
      this.form.get('tenant').setValue(this.tenant);
      this.loadDefaultPartner(this.tenant);
    }
  }

  /**
   * Affilié par défaut du tenant, aligné sur l'étape de paramétrage des imports
   * (etl-update-setup.component.ts).
   */
  private loadDefaultPartner(tenant: string): void {
    if (!tenant) {
      this.defaultPartnerCode = null;
      return;
    }
    this.configuratorDatastoreService.getDatastoreUsingGET(tenant, true).subscribe(datastore => {
      this.defaultPartnerCode = datastore?.defaultPartner || null;
      this.applyDefaultAffectedPartner();
    });
  }

  /**
   * À la création, l'export est affecté à l'affilié par défaut du tenant.
   *
   * Sans affilié affecté, l'étape Recherche n'a aucun périmètre déterministe pour prévisualiser
   * les données et retombe sur un affilié arbitraire de l'utilisateur. L'affectation enregistrée
   * d'un export existant n'est jamais écrasée.
   */
  private applyDefaultAffectedPartner(): void {
    if (!this.newEtl || !this.defaultPartnerCode) {
      return;
    }
    const affected = this.parent.affectedPartners ?? [];
    if (!affected.includes(this.defaultPartnerCode)) {
      this.parent.affectedPartners = [this.defaultPartnerCode, ...affected];
    }
  }

  private subscribeToEtlChanges(): void {
    if (!this.parent) {
      return;
    }

    if (this.currentEtl?.extract.typeObject === 'SaveQueryExtractConf') {
      const saveQueryConf = this.currentEtl.extract as SaveQueryExtractConf;
      if (this.isAuthorizedAdmin && !this.currentEtl?.partner) {
        this.form.patchValue({
          id: this.currentEtl.id,
          name: this.currentEtl.name,
          description: this.currentEtl.description,
          tenant: this.form.get('tenant').value || this.parent.tenant,
          partner: '',
          view: saveQueryConf.saveQuery.view
        });
        this.form.get('partner')?.disable();        
      } else {
        this.form.patchValue({
          id: this.currentEtl.id,
          name: this.currentEtl.name,
          description: this.currentEtl.description,
          partner: this.currentEtl.partner,
          tenant: this.form.get('tenant').value || this.parent.tenant,
          view: saveQueryConf.saveQuery.view
        });
      }
      this.form.patchValue({
        exportPartners: this.currentEtl.exportPartners ?? [],
        exportMode: this.currentEtl.exportMode ?? EtlConf.ExportModeEnum.GROUPED
      });
      if (this.isAuthorizedAdmin) {
        this.actualPartner = "";
      } else {
        this.actualPartner = this.currentEtl.partner;
      }
      this.actualOwner = this.currentEtl.owner;
    }
  }

  private handleTenantChanges(): void {
    this.form.get('tenant').valueChanges.subscribe((val) => {
      if (this.tenant != val) {
        this.tenant = val;
        // Les affiliés affectés appartiennent au tenant précédent : ils n'existent pas
        // forcément dans le nouveau et ne doivent pas être enregistrés tels quels.
        this.parent.affectedPartners = [];
        this.form.get('exportPartners').setValue([]);
        this.loadDefaultPartner(val);
        this.accountService.fetch().subscribe(account => {
          this.isAuthorizedAdmin = isAdminOfTenant(account, this.tenant);
          this.form.get('view').setValue('');
          if (this.isAuthorizedAdmin) {
            this.form.get('partner')?.disable();
            this.actualPartner = "";
          } else {
            this.form.get('partner')?.enable();
            if (this.currentEtl?.partner) {
              this.actualPartner = this.currentEtl.partner;
              this.form.get('partner')?.setValue(this.actualPartner);
            }
          }
        });
      }
    });
  }


  saveStepDescription() {
    const value = this.form.value;
    const view = typeof value.view === "object" ? value.view.value : value.view;
    let owner: string;
    if (this.actualOwner) {
      owner = this.actualOwner;
    } else {
      if (this.isAuthorizedAdmin) {
        owner = "admin";
      } else if (value?.partner) {
        owner = value.partner;
      } else {
        owner = this.actualPartner;
      }
    }
    let dto: EtlExportDescDTO;
    dto = {
      description: value.description,
      partner: value?.partner ? value.partner : this.actualPartner ?? "",
      affectedPartners: this.parent.affectedPartners ?? [],
      exportPartners: this.isTenantExport ? value.exportPartners : undefined,
      exportMode: this.isTenantExport ? value.exportMode : undefined,
      owner: owner,
      view: view,
      name: value.name,
      tenant: value.tenant,
      id: value.id
    };
    this.configuratorEtlService.updateExportDescription(dto, dto.tenant).pipe(
      switchMap((id) => {
        this.parent.id = id.value;
        this.parent.tenant = value.tenant;
        // Rediffuser l'ETL fraîchement enregistré avant de naviguer : les étapes suivantes
        // lisent parent.etl$, qui conserverait sinon l'affectation d'affiliés précédente.
        return this.parent.reloadEtl();
      })
    ).subscribe(() => {
      this.parent.goToNextStep();
    });
  }

  /** Export du tenant : il choisit ses affiliés ; l'export d'un affilié ne porte que sur ses propres données. */
  get isTenantExport(): boolean {
    return !!this.isAuthorizedAdmin && !this.currentEtl?.partner;
  }

  /** Le mode séparé n'a de sens qu'à partir de deux affiliés : en dessous, l'export est groupé. */
  private applyExportModeAvailability(): void {
    const mode = this.form.get('exportMode');
    if ((this.form.get('exportPartners').value ?? []).length < 2) {
      mode.setValue(EtlConf.ExportModeEnum.GROUPED, {emitEvent: false});
      mode.disable({emitEvent: false});
    } else {
      mode.enable({emitEvent: false});
    }
  }

  get hasExportPartners(): boolean {
    return (this.form?.get('exportPartners').value ?? []).length > 0;
  }

  get scopeChangeAffectsChannels(): boolean {
    const stores = (this.currentEtl?.processingLoad as FileProcessingLoadConf)?.stores ?? [];
    if (!this.currentEtl || stores.length === 0) {
      return false;
    }
    const selected: string[] = this.form.get('exportPartners').value ?? [];
    const saved = this.currentEtl.exportPartners ?? [];
    const samePartners = selected.length === saved.length && selected.every(p => saved.includes(p));
    const sameMode = this.form.get('exportMode').value === (this.currentEtl.exportMode ?? EtlConf.ExportModeEnum.GROUPED);
    return !samePartners || !sameMode;
  }

  updateAffectedPartners($event) {
    this.parent.affectedPartners = $event;
  }
}
